import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

import { useStyles } from './DetailSection.styles';

export interface DetailSectionEditProps {
    isEditing: boolean;
    canSave: boolean;
    isSaving: boolean;
    onEdit: () => void;
    onCancel: () => void;
    onSave: () => void;
    editLabel: string;
    cancelLabel: string;
    saveLabel: string;
}

export interface DetailSectionProps {
    automationId: string;
    title?: string;
    description?: string;
    /** Replace the default edit/cancel/save icon cluster with custom actions. */
    actions?: ReactNode;
    /** Driven edit toggle; omit to render a static section with no controls. */
    edit?: DetailSectionEditProps;
    children: ReactNode;
}

/**
 * Card-style section with a title row and content. When `edit` is provided,
 * renders a pencil icon that swaps to cancel/save icons in edit mode.
 */
export const DetailSection = ({
    automationId,
    title,
    description,
    actions,
    edit,
    children,
}: DetailSectionProps) => {
    const { classes } = useStyles();

    const renderEditControls = () => {
        if (!edit) return null;
        if (!edit.isEditing) {
            return (
                <Tooltip title={edit.editLabel}>
                    <IconButton
                        size="small"
                        onClick={edit.onEdit}
                        aria-label={edit.editLabel}
                        automation-id={`${automationId}-edit`}
                    >
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            );
        }
        return (
            <>
                <Tooltip title={edit.cancelLabel}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={edit.onCancel}
                            disabled={edit.isSaving}
                            aria-label={edit.cancelLabel}
                            automation-id={`${automationId}-cancel`}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={edit.saveLabel}>
                    <span>
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={edit.onSave}
                            disabled={!edit.canSave || edit.isSaving}
                            aria-label={edit.saveLabel}
                            automation-id={`${automationId}-save`}
                        >
                            {edit.isSaving ? (
                                <CircularProgress
                                    size={16}
                                    automation-id={`${automationId}-save-spinner`}
                                />
                            ) : (
                                <CheckIcon fontSize="small" />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </>
        );
    };

    const hasTitle = Boolean(title || description);

    return (
        <Box className={classes.root} automation-id={automationId}>
            {hasTitle ? (
                <Box className={classes.header}>
                    <Box className={classes.titleGroup}>
                        {title && <Typography variant="h6">{title}</Typography>}
                        {description && (
                            <Typography variant="body2" className={classes.description}>
                                {description}
                            </Typography>
                        )}
                    </Box>
                    <Box className={classes.actions}>
                        {actions}
                        {renderEditControls()}
                    </Box>
                </Box>
            ) : (
                <Box className={classes.actionsFloating}>
                    {actions}
                    {renderEditControls()}
                </Box>
            )}
            {children}
        </Box>
    );
};
